import {useEffect, useRef, useState} from 'react';
import type {MouseEvent} from 'react';
import './FenzSafeGuestEntry.css';
import {FenzSafeApp} from './FenzSafeApp';
import {hasReachedFirstValue, resetGuestFenzSafeStore, useFenzSafe} from './fenzsafeStore';
import {trackFenzSafeGuestEntry} from './fenzsafeFunnelEvents';
import {authJourneyUrl} from './authReturnDestination';
import {cleanupGuestClaimStorage, readGuestClaimSnapshot, saveGuestClaimSnapshot} from './fenzsafeGuestClaim';

/**
 * Issue #192 Docket #19 follow-up: the one-line producer half of PR #254's `guest_conversion=1`
 * signup-framing marker (see that PR's own "Unresolved / follow-up" note and `PublicSignupApp.tsx`'s
 * matching consumer check). This is a presentation hint only -- it lets the customer-signup page
 * show "continue your FenzSafe matter" copy after this component has confirmed a claimable matter
 * snapshot on the device. It is never a security/ownership proof. The static/native-link fallback
 * deliberately omits the marker because it bypasses the save handler. Appended the same way
 * `preserveProfenzHandoffQuery()` appends its own query param, so it composes correctly with
 * `authJourneyUrl()`'s optional `return_to`.
 */
function guestConversionSignUpUrl(): string {
  const target = authJourneyUrl('/sign-up');
  const separator = target.includes('?') ? '&' : '?';
  return `${target}${separator}guest_conversion=1`;
}

/** Guest work stays in memory until the visitor explicitly starts account conversion.
 * Wait for confirmed browser storage before leaving. Import after login requires separate consent. */
export function FenzSafeGuestEntry() {
  const busy = useRef(false);
  // cleanupGuestClaimStorage() and saveGuestClaimSnapshot() both take the same exclusive
  // navigator.locks lock (fenzsafeGuestClaim.ts's exclusively()), which uses {ifAvailable: true}
  // and fails closed rather than queuing. The mount effect below fires cleanup as fire-and-forget,
  // so a visitor who clicks "Create a free account" fast enough (nothing to type or upload first)
  // can reach saveGuestClaimSnapshot() while that cleanup call still holds the lock, throwing
  // "Guest work is being saved in another tab" for a guest who never had anything to save.
  // Awaiting this ref before saving serializes the two calls within this same page load without
  // changing the lock's actual cross-tab semantics.
  const cleanupRef = useRef<Promise<void>>(Promise.resolve());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Docket B13 (Issue #192 comment 5860340094, closing B3 Finding C): this banner used to be
  // static from first paint regardless of anything the guest actually did. hasReachedFirstValue()
  // (Docket B12) is a truthful signal the guest has genuinely reviewed/organized something, not
  // merely uploaded a file -- once true, the still-true "nothing is saved yet" warning becomes a
  // progress-aware nudge instead of a generic one.
  const state = useFenzSafe();
  const hasProgress = state.matters.some(hasReachedFirstValue);
  useEffect(() => {
    resetGuestFenzSafeStore();
    trackFenzSafeGuestEntry();
    cleanupRef.current = cleanupGuestClaimStorage().catch(() => undefined);
  }, []);

  async function handleCreateAccountClick(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    if (busy.current) return;
    busy.current = true; setSaving(true); setError('');
    try {
      await cleanupRef.current;
      await saveGuestClaimSnapshot();
      // Only a real, durably readable matter snapshot may select guest-continuation framing.
      // A blank guest can still create an account, but must receive ordinary signup copy.
      const hasClaimableMatter = (readGuestClaimSnapshot()?.length ?? 0) > 0;
      window.location.assign(hasClaimableMatter ? guestConversionSignUpUrl() : authJourneyUrl('/sign-up'));
    } catch {
      setError('Your guest work could not be saved in this browser. Keep this tab open; your work is still here. Try again before continuing to signup.');
    } finally { busy.current = false; setSaving(false); }
  }

  return (
    <div className="fge-root">
      <div className="fge-banner" role="status">
        <p className="fge-banner-text">
          {hasProgress ? (
            <><strong>You’ve reviewed your first document — nice progress.</strong> It still isn’t saved: this browser
            tab is the only copy, and it’s lost if you leave or reload the page.</>
          ) : (
            <><strong>You’re trying FenzSafe as a guest.</strong> Nothing you organize here is saved yet — it lives only in
            this browser tab and is lost if you leave or reload the page.</>
          )}
        </p>
        <a aria-disabled={saving} className="fge-cta" href={authJourneyUrl('/sign-up')} onClick={(event) => void handleCreateAccountClick(event)}>
          {hasProgress ? 'Create a free account to save what you’ve organized' : 'Create a free account to save your work'}
        </a>
      </div>
      <p>Choosing create account stores a temporary copy on this device for up to 48 hours. After signing in, confirm the account to save it into. Temporary copies are cleaned up when you next open FenzSafe.</p>
      {error && <p role="alert">{error}</p>}
      <FenzSafeApp />
    </div>
  );
}
